import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Link, NavLink, useLocation } from 'react-router-dom';

import plusCircleImage from '@/components/common/icons/plus-circle.svg';
import usersIcon from '@/components/common/icons/users.svg';
import { orderType as parseOrderType } from '@/constants';
import InspectorsIcon from '@/svg-icons/inspectors';
import SettingsIcon from '@/svg-icons/menu-settings';

const CommercialSidebar = () => {
  const intl = useIntl();
  const { pathname } = useLocation();
  const page = pathname.split('/')[2];
  const me = JSON.parse(window.sessionStorage.getItem('me'));
  const features = me?.features?.map(feature => parseOrderType.get(feature));

  const menu = useMemo(
    () => ({
      clients: {
        icon: <InspectorsIcon />,
        title: intl.formatMessage({ id: 'COMMERCIAL' }),
        link: '/commercial/clients',
        actions: [
          {
            link: '/commercial/clients/new',
            icon: plusCircleImage,
            title: intl.formatMessage({ id: 'ADD_CUSTOMER' }),
          },
        ],
        children: [
          {
            link: '/commercial/clients',
            title: intl.formatMessage({ id: 'CUSTOMERS' }),
            icon: usersIcon,
          },
        ],
      },
      settings: {
        icon: <SettingsIcon />,
        title: intl.formatMessage({ id: 'SETTINGS' }),
        link: '/commercial/settings',
        requiredFeature: 'user',
        actions: [
          {
            link: '/commercial/settings/users/new',
            icon: plusCircleImage,
            title: intl.formatMessage({ id: 'ADD_USER' }),
          },
        ],
        children: [
          {
            link: '/commercial/settings/users',
            icon: usersIcon,
            title: intl.formatMessage({ id: 'USERS' }),
          },
        ],
      },
    }),
    [intl]
  );

  const activeMenu = useMemo(() => menu[page] || null, [menu, page]);

  return (
    <aside className="relative flex flex-col flex-shrink-0 min-h-[330px] min-w-[220px] max-w-[220px] w-[220px] pt-[1.55rem] pb-[1.15rem] px-[1.15rem] bg-[#1a3a5f] text-white text-[14px]">
      <h3 className="flex items-center !text-[1.1em] font-bold text-[#5578a0] mb-[0.5rem] min-h-[34px] leading-none">
        <span className="flex justify-center items-center mr-[0.5em] rounded-full shrink-0">
          {activeMenu && React.cloneElement(activeMenu.icon, { className: 'w-[34px] h-[34px]' })}
        </span>
        <span className="flex-1">{activeMenu && activeMenu.title}</span>
        {activeMenu && activeMenu.actions && (
          <ul className="flex items-center justify-end list-none m-0 p-0 shrink-0 ml-auto">
            {activeMenu.actions.map(action => {
              if (action.requiredFeature && features?.indexOf(action.requiredFeature) < 0) return null;
              return (
                <li className="ml-[8px] flex items-center" key={action.link}>
                  <Link to={action.link} className="flex items-center justify-center w-[24px] h-[24px] transition-opacity hover:opacity-75 focus:outline-0 !no-underline">
                    <img src={action.icon} alt={action.title} className="max-w-full max-h-full" />
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </h3>

      <ul className="list-none m-0 p-0">
        {activeMenu &&
          activeMenu.children.map(child => (
            <li key={child.link} className="my-[10px] first:mt-0 last:mb-0">
              <NavLink 
                to={child.link} 
                className={({ isActive }) => 
                  `flex items-center h-[3em] -mx-[1.15rem] px-[1.15rem] bg-none leading-none !no-underline text-white transition-all border-l-[4px] border-l-transparent ${
                    isActive ? 'bg-[#153253] border-l-white hover:bg-[#102d4e]' : 'hover:bg-[#15355a]'
                  }`
                }
              >
                <span className="w-[28px] h-[28px] mr-[0.75em] leading-[0] flex items-center justify-center">
                  {typeof child.icon === 'string' ? (
                    <img src={child.icon} alt={child.title} className="w-full h-full" />
                  ) : (
                    React.cloneElement(child.icon, { className: 'text-[28px] w-[28px] h-[28px]' })
                  )}
                </span>
                {child.title}
              </NavLink>
            </li>
          ))}
      </ul>

      <ul className="list-none m-0 p-0 mt-auto">
        {Object.keys(menu).map(key => {
          if (key === page) return null;
          if (menu[key].requiredFeature && features?.indexOf(menu[key].requiredFeature) < 0) return null;

          return (
            <li key={key} className="my-[10px] first:mt-0 last:mb-0 relative w-full">
              {menu[key].link ? (
                <NavLink 
                  to={menu[key].link} 
                  className={({ isActive }) => 
                    `flex items-center h-[3em] font-bold !text-[1.1em] !text-[#5578a0] bg-none border-0 !no-underline transition-all px-[1.15em] hover:bg-[#15355a] ${
                      isActive ? 'bg-[#153253]' : ''
                    }`
                  }
                >
                  <span className="relative w-[34px] h-[34px] mr-[0.5em] flex items-center justify-center">
                    {React.cloneElement(menu[key].icon, { className: 'w-[34px] h-[34px]' })}
                  </span>
                  {menu[key].title}
                </NavLink>
              ) : (
                <a
                  href="/"
                  className="flex items-center h-[3em] font-bold !text-[1.1em] !text-[#5578a0] bg-none border-0 !no-underline transition-all px-[1.15em] hover:bg-[#15355a]"
                  onClick={e => {
                    e.preventDefault();
                    if (menu[key]?.onClick && typeof menu[key]?.onClick === 'function') {
                      menu[key].onClick();
                    }
                  }}
                >
                  <span className="relative w-[34px] h-[34px] mr-[0.5em] flex items-center justify-center">
                    {React.cloneElement(menu[key].icon, { className: 'w-[34px] h-[34px]' })}
                  </span>
                  {menu[key].title}
                </a>
              )}
            </li>
          );
        })}
      </ul>
    </aside>
  );
};

export default CommercialSidebar;
